
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';
import { NodeType } from '@amplify/amp-module';
import NodeWrapper from '@/components/graph/common/NodeWrapper.vue';
import AddLinkPopup from '@/components/graph/popups/AddLinkPopup.vue';
import AddVideoPopup from '@/components/graph/popups/AddVideoPopup.vue';
import { URLRegex, vimeoRegex, youtubeRegex } from '@/constants/regularExpressions';
import { debounce } from '@/utils/debounce';
import { useStoreInNodes } from '@/composables/useStoreInNodes';
import { ToggleMediaUploadPopup } from '@/constants/mutations';

export default {
  components: { AddVideoPopup, AddLinkPopup, NodeWrapper },

  /**
   * id - the UUID of the current sub node
   * index - the index of the sub node inside the group node
   * data - the current sub node's data
   * parentId - the current sub node's parent UUID
   * selected - indicates if the sub node is selected
   * blurred - indicates if the sub node is blurred
   * readonly - indicates if the sub node is readonly
   */
  props: {
    id: { type: String, default: '' },
    index: { type: Number, default: -1 },
    data: { type: Object, default: () => ({}) },
    parentId: { type: String, default: '' },
    selected: { type: Boolean, default: false },
    blurred: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
  },

  setup(props, { emit }) {
    const nodeType = NodeType.carousel;

    const { store } = useStoreInNodes();

    const getGraph = inject('getGraph', () => {});
    const getNode = inject('getNode', () => {});

    const hovered = ref(false);
    const isLinkHovered = ref(false);
    const isMediaHovered = ref(false);
    const showLinkPopup = ref(false);
    const showVideoPopup = ref(false);
    const headline = ref('');
    const description = ref('');
    const callbacks = ref({});
    const readyElements = ref(0);
    const nbOfElements = ref(3); // 2 text areas and 1 image/iframe

    const uiData = computed(() => ({ hovered: hovered.value, selected: props.selected }));

    const hasMedia = computed(() => props.data.media?.url && props.data.media?.type && validMediaURL.value);
    const hasLink = computed(() => props.data.link?.url && props.data.link?.title && validLinkURL.value);
    const hasEmail = computed(() => props.data.email?.address && props.data.email?.title);

    const validMediaURL = computed(() => !!validateURL(props.data.media?.url || ''));
    const validLinkURL = computed(() => !!validateURL(props.data.link?.url || ''));

    const imageURL = computed(() =>
      props.data?.media?.url?.replace(/\/image\/upload\//g, '$&c_fill,g_auto,w_456,h_264/')
    );
    const videoURL = computed(() => {
      const { url } = props.data.media;
      const matchYoutube = url.match(youtubeRegex);

      if (matchYoutube) {
        return `https://youtube.com/embed/${matchYoutube[1]}`;
      }

      const matchVimeo = url.match(vimeoRegex);

      if (matchVimeo) {
        return `https://player.vimeo.com/video/${url.replace(/.*vimeo.com\/(.*)/g, '$1')}`;
      }

      return '';
    });
    const linkURLDomain = computed(() => {
      const [domain] = (props.data.link?.url || '').match(/^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/);

      return (domain || '').replace(/^https?:\/\//, '');
    });

    const isDone = computed(
      () => !!(props.data.headline && props.data.description && (hasMedia.value || hasLink.value || hasEmail.value))
    );

    watch(
      props.data,
      (newData) => {
        description.value = newData.description;
        headline.value = newData.headline;
      },
      { deep: true }
    );

    onMounted(async () => {
      description.value = props.data.description;
      headline.value = props.data.headline;

      // update the numbers of elements we have to wait to be ready
      nbOfElements.value = [props.data.media?.url, headline.value, description.value].reduce(
        (counter, value) =>
          // if value exists we need to wait for the element to be ready
          value ? counter + 1 : counter,
        0
      );

      if (!nbOfElements.value) {
        await nextTick();
        emit('initial-resize');
      }
    });

    const onTextChange = (e) => {
      if (props.readonly) {
        return;
      }

      const { name, value } = e.target;
      const callback = `${name}_callback`;

      if (callbacks.value[callback]) {
        callbacks.value[callback].cancel();
      }

      callbacks.value[callback] = debounce(() => {
        emit('update-node-data', { ...props.data, [name]: value });
      }).execute();
    };

    const onDeleteEmailAndLink = () => {
      emit('update-node-data', {
        ...props.data,
        email: { address: '', subject: '', title: '' },
        link: { url: '', title: '' },
      });
    };

    const onDeleteMedia = () => {
      emit('update-node-data', { ...props.data, media: { type: '', url: '', title: '' } });
    };

    const onVideoChange = ({ url, title }) => {
      if (callbacks.value.video) {
        callbacks.value.video.cancel();
      }

      callbacks.value.video = debounce(() => {
        emit('update-node-data', { ...props.data, media: { type: 'video', url, title } });
      }).execute();
    };

    const onEmailAndLinkChange = (changes) => {
      const { email, link } = changes;

      if (callbacks.value.link) {
        callbacks.value.link.cancel();
      }

      callbacks.value.link = debounce(() => {
        emit('update-node-data', { ...props.data, email: { ...email }, link: { ...link } });
      }).execute();
    };

    const onElementReady = () => {
      readyElements.value += 1;

      if (readyElements.value === nbOfElements.value) {
        emit('initial-resize');
      }
    };

    const onHeadlineKeyDown = (e) => {
      // Disable newline in headline
      if (e.keyCode === 13 || e.key.toLowerCase() === 'enter') {
        e.preventDefault();
      }
    };

    // Popup Handling
    const showMediaPopup = () => {
      if (props.readonly) {
        return;
      }

      emit('show-media-popup');
      store.value.commit(`popupStore/${ToggleMediaUploadPopup}`, true);
    };

    const toggleVideoPopup = () => {
      if (props.readonly) {
        return;
      }

      showVideoPopup.value = !showVideoPopup.value;
      bringNodeToFront(showVideoPopup.value);
    };

    const onEditMedia = () => {
      if (props.readonly) {
        return;
      }

      props.data.media.type === 'video' ? toggleVideoPopup() : showMediaPopup();
      bringNodeToFront(showVideoPopup.value);
    };

    const toggleLinkPopup = () => {
      if (props.readonly) {
        return;
      }

      showLinkPopup.value = !showLinkPopup.value;
      bringNodeToFront(showLinkPopup.value);
    };

    const openInNewTab = (url) => window.open(url, '_blank').focus();

    const sendEmail = (address, subject = '') => (window.location.href = `mailto:${address}?subject=${subject}`);

    const validateURL = (url) => url.match(URLRegex);

    const bringNodeToFront = (moveOnTop) => {
      if (moveOnTop) {
        getGraph()?.getCellById(getNode().id)?.toFront();
      }
    };

    return {
      description,
      emit,
      hasEmail,
      hasLink,
      hasMedia,
      headline,
      hovered,
      imageURL,
      isDone,
      isLinkHovered,
      isMediaHovered,
      linkURLDomain,
      nodeType,
      showLinkPopup,
      showVideoPopup,
      uiData,
      videoURL,
      onDeleteEmailAndLink,
      onDeleteMedia,
      onEditMedia,
      onEmailAndLinkChange,
      onElementReady,
      onHeadlineKeyDown,
      onTextChange,
      onVideoChange,
      openInNewTab,
      sendEmail,
      showMediaPopup,
      toggleLinkPopup,
      toggleVideoPopup,
    };
  },
};
